<script lang="ts">
  import { onMount } from "svelte";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import CartWidgetGroupV2 from "@retailer/svelte/CartWidget/v2/CartWidgetGroupV2.svelte";
  import CartWidgetNotice from "@retailer/svelte/CartWidget/v2/CartWidgetNotice.svelte";
  import RequestItem from "@retailer/svelte/CartWidget/v2/middleware/RequestItem.svelte";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import CartStore from "@retailer/svelte/stores/CartStore";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import {
    sendCartTrackingEvents,
    sendClickTracking,
  } from "@retailer/svelte/CartWidget/v2/Services/CartEventTracking";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { CartWidgetTargetObjectBase } from "@shared/services/event_tracking/entities";
  import {
    shouldUseRetailerFulfillmentMethodForDistributor,
    hideFulfillmentMethodPickerForDistributor,
  } from "@retailer/services/fulfillment_methods_service";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  import type {
    Notice,
    CartProductUpdateParams,
    BuyingOption,
    DistributorSummary,
  } from "@retailer/svelte/CartWidget/v2/Services/CartItemRequestService";
  import type { BaseEntityClass } from "@shared/services/event_tracking/entities/base_entity_class";
  import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";

  type SuggestedQuantities = {
    caseQuantity: number;
    unitQuantity: number;
    containerType: string;
  };

  export let inventoryId: number;
  export let trackingEntities: BaseEntityClass[] = [];
  export let suggestedQuantities: SuggestedQuantities | null = null;

  const targetObjectTypeBase = CartWidgetTargetObjectBase.RepRecommendation;
  const { trackClick } = EventTracking;

  let loaded: boolean = false;

  onMount(async () => {
    loaded = false;
    const response = await InventoryItemStore.fetchInventoryItem(inventoryId);
    loaded = response?.loaded;
  });

  const trackCartChangeEvent = event => {
    sendCartTrackingEvents(event, trackingEntities, inventoryId);
  };

  const trackCartInteractionEvent = event => {
    sendClickTracking(targetObjectTypeBase, event, trackingEntities);
  };

  const trackAddSuggestedEvents = (originalBuyingOptions: BuyingOption[]) => {
    trackClick("add_to_cart", trackingEntities);

    for (const buyingOption of originalBuyingOptions) {
      if (buyingOption?.quantity_type === "case" && suggestedQuantities?.caseQuantity > 0) {
        let action = buyingOption.quantity < suggestedQuantities.caseQuantity ? "add" : "remove";
        trackCartChangeEvent({
          detail: {
            oldQuantity: buyingOption.quantity,
            newQuantity: suggestedQuantities.caseQuantity,
            quantityType: "case",
            eventName: `cart_${action}`,
          },
        });
      } else if (buyingOption?.quantity_type === "unit" && suggestedQuantities?.unitQuantity > 0) {
        let action = buyingOption.quantity < suggestedQuantities.unitQuantity ? "add" : "remove";
        trackCartChangeEvent({
          detail: {
            oldQuantity: buyingOption.quantity,
            newQuantity: suggestedQuantities.unitQuantity,
            quantityType: "unit",
            eventName: `cart_${action}`,
          },
        });
      }
    }
  };

  const suggestedQuantitiesMatch = () => {
    if (suggestedQuantities?.caseQuantity || suggestedQuantities?.caseQuantity === 0) {
      const caseOption = buyingOptions.find(option => option?.quantity_type === "case");
      if (caseOption && suggestedQuantities.caseQuantity !== caseOption.quantity) return false;
    }

    if (suggestedQuantities?.unitQuantity || suggestedQuantities?.unitQuantity === 0) {
      const unitOption = buyingOptions.find(option => option?.quantity_type === "unit");
      if (unitOption && suggestedQuantities.unitQuantity !== unitOption.quantity) return false;
    }

    return true;
  };

  let isSubmittingAddSuggested = false;
  const addSuggested = async () => {
    const cartProduct: CartProductUpdateParams = {
      inventory_id: inventoryId,
      case_quantity: suggestedQuantities.caseQuantity,
      unit_quantity: suggestedQuantities.unitQuantity,
    };
    // We want the original quantities, but may need to add to cart to create a trackable order
    const originalBuyingOptions = buyingOptions;

    isSubmittingAddSuggested = true;
    await InventoryItemStore.addToCart(inventoryId, cartProduct);
    await CartStore.load();
    trackAddSuggestedEvents(originalBuyingOptions);
    isSubmittingAddSuggested = false;
  };

  const hasFullfillmentMethodsRollout = stagedRolloutCheck("marketplace__fulfillment_methods");
  let fulfillmentMethod: RetailerFulfillmentMethod = null;
  let fulfillmentModel: string = null;
  let distributorSummary: DistributorSummary;
  let distributorSummaryId: number;

  $: cartWidgetItem = $InventoryItemStore?.inventoryItems[inventoryId];
  $: buyingOptions = cartWidgetItem?.buying_options || [];
  $: distributorSummary = cartWidgetItem?.distributor_summary;
  $: distributorSummaryId = distributorSummary?.id;
  $: {
    if (
      hideFulfillmentMethodPickerForDistributor(distributorSummaryId) &&
      fulfillmentModel === "pickup_only"
    ) {
      fulfillmentMethod = $RetailerFulfillmentMethodsStore[distributorSummaryId] || null;
    }
  }
  $: usesFulfillmentMethodBanner =
    shouldUseRetailerFulfillmentMethodForDistributor(
      distributorSummaryId,
      distributorSummary?.uses_warehouse_pickup,
      hasFullfillmentMethodsRollout && distributorSummary?.uses_wine_cellar,
    ) && !hideFulfillmentMethodPickerForDistributor(distributorSummaryId);
  $: {
    if (usesFulfillmentMethodBanner) {
      fulfillmentMethod = $RetailerFulfillmentMethodsStore[distributorSummaryId] || null;
      InventoryItemStore.fetchInventoryItem(inventoryId);
    }
  }
  $: fulfillmentWarehouseId =
    fulfillmentMethod?.warehouse_id || cartWidgetItem?.distributor_summary?.default_warehouse_id;
  $: fulfillmentModel = cartWidgetItem?.distributor_summary?.fulfillment_model;
  $: informationalNotices = cartWidgetItem?.notices.filter((notice: Notice) => {
    return notice.level === "info" && notice.message;
  });
  $: notices = cartWidgetItem?.notices.filter((notice: Notice) => {
    return notice.level !== "info" && notice.message;
  });
  $: addSuggestedDisabled =
    isSubmittingAddSuggested || buyingOptions.length < 1 || suggestedQuantitiesMatch();
</script>

{#if loaded && cartWidgetItem}
  <RequestItem {cartWidgetItem} {fulfillmentWarehouseId}>
    <div class="fizz-flow-8" slot="cart-widget">
      {#each notices as notice}
        <CartWidgetNotice {notice} />
      {/each}
      <CartWidgetGroupV2
        widgetItem={cartWidgetItem}
        {fulfillmentMethod}
        {fulfillmentWarehouseId}
        on:cartWidgetChange={trackCartChangeEvent}
        on:updateQuantity={trackCartInteractionEvent}
      />
      {#each informationalNotices as notice}
        <CartWidgetNotice {notice} />
      {/each}
      {#if suggestedQuantities}
        <div>
          <button
            class="fizz-button fizz-button-medium fizz-full-width fizz-stack-4"
            disabled={addSuggestedDisabled}
            on:click={addSuggested}
          >
            Add Suggested Quantities to Cart
          </button>
          <p class="fizz-text-small fizz-text-center">
            Suggested:
            {#if suggestedQuantities.unitQuantity}
              {pluralize(
                suggestedQuantities.containerType,
                suggestedQuantities.unitQuantity,
                true,
              )}{#if suggestedQuantities.caseQuantity},{/if}
            {/if}
            {#if suggestedQuantities.caseQuantity}
              {pluralize("case", suggestedQuantities.caseQuantity, true)}
            {/if}
          </p>
        </div>
      {/if}
    </div>
  </RequestItem>
{/if}
